/**
 * API URL-константы EyeHub. Централизованное хранилище путей endpoint'ов.
 *
 * Все пути — относительно baseURL (REACT_APP_API_URL, по умолчанию
 * http://localhost:8000/api/v1/). Соглашение — kebab-case, всё под /api/v1/.
 *
 * @module api/constants
 */

// ============================================================================
// Base API Configuration
// ============================================================================

/** Базовый путь API. Используется в raw-fetch (auth-сервис). */
export const API_BASE_PATH = "/api/v1/"

// ============================================================================
// Authentication (BFF / Keycloak PKCE) — портал использует SSO-only
// ============================================================================

export const AUTH_ENDPOINTS = {
  // Keycloak SSO entry point (browser redirect к Keycloak)
  KEYCLOAK_LOGIN: "auth/keycloak/login/",
  KEYCLOAK_CALLBACK: "auth/keycloak/callback/",
  // Унифицированные auth-endpoints (читают cookie-сессию eyehub_session)
  STATUS: "auth/status/", // GET — user + authenticated
  LOGOUT: "auth/logout/", // POST — завершение сессии (+ Keycloak SLO)
}

/** Полные пути для прямых browser-редиректов (window.location). */
export const AUTH_FULL_PATHS = {
  KEYCLOAK_LOGIN: "/api/v1/auth/keycloak/login/",
  KEYCLOAK_CALLBACK: "/api/v1/auth/keycloak/callback/",
}

// ============================================================================
// Hub resource endpoints (per BUILD_BRIEF §Модель данных)
// ============================================================================

/** Организации / площадки / инсталляции / домены / членство. */
export const ORGANIZATION_ENDPOINTS = {
  LIST: "organizations/",
  DETAIL: id => `organizations/${id}/`,
  CREATE: "organizations/",
  UPDATE: id => `organizations/${id}/`,
}

export const SITE_ENDPOINTS = {
  LIST: "sites/",
  DETAIL: id => `sites/${id}/`,
  CREATE: "sites/",
}

/** Публичный поиск площадок для пациента (без PHI). */
export const FINDCLINIC_ENDPOINTS = {
  PUBLIC_CLINICS: "public/clinics/",
  PUBLIC_FUNNEL: "public/funnel/",
  PUBLIC_INVITE: code => `public/invites/${code}/`,
  MY_CLINICS: "my-clinics/",
  INVITES: "clinic-invites/",
  INVITE_REVOKE: id => `clinic-invites/${id}/revoke/`,
  FUNNEL_COUNTERS: id => `sites/${id}/funnel-counters/`,
}

export const INSTALLATION_ENDPOINTS = {
  LIST: "installations/",
  DETAIL: id => `installations/${id}/`,
  CREATE: "installations/",
  SSH_GRANT: id => `installations/${id}/ssh-grants/`,
  ISO_GRANT: id => `installations/${id}/iso-grants/`,
}

export const ORG_DOMAIN_ENDPOINTS = {
  LIST: "org-domains/",
  DETAIL: id => `org-domains/${id}/`,
  CREATE: "org-domains/",
  // Верификация домена (TXT-токен) — POST org-domains/{id}/verify/
  VERIFY: id => `org-domains/${id}/verify/`,
  // Отзыв домена — PATCH org-domains/{id}/ {status:"revoked"} (fallback — DELETE).
  REVOKE: id => `org-domains/${id}/revoke/`,
}

export const ORG_MEMBERSHIP_ENDPOINTS = {
  LIST: "org-memberships/",
  DETAIL: id => `org-memberships/${id}/`,
  CREATE: "org-memberships/",
  // Деактивация членства — POST org-memberships/{id}/deactivate/
  DEACTIVATE: id => `org-memberships/${id}/deactivate/`,
}

/** Enrollment коннекторов (выпуск/одобрение токенов). */
export const ENROLLMENT_TOKEN_ENDPOINTS = {
  LIST: "enrollment-tokens/",
  DETAIL: id => `enrollment-tokens/${id}/`,
  CREATE: "enrollment-tokens/",
  APPROVE: id => `enrollment-tokens/${id}/approve/`,
  // Отзыв выпущенного токена (утёкший bearer-секрет) — POST enrollment-tokens/{id}/revoke/
  REVOKE: id => `enrollment-tokens/${id}/revoke/`,
}

/** Коннекторы (identities on-prem инсталляций) + fleet-обзор. */
export const CONNECTOR_ENDPOINTS = {
  LIST: "connectors/",
  DETAIL: id => `connectors/${id}/`,
  // Отзыв коннектора (IsHubAdmin) — POST connectors/{id}/revoke/
  REVOKE: id => `connectors/${id}/revoke/`,
}

export const FLEET_ENDPOINTS = {
  // Снимки телеметрии инсталляций (реальный путь — kebab-case ресурс роутера).
  SNAPSHOTS: "fleet-snapshots/",
  SNAPSHOT_DETAIL: id => `fleet-snapshots/${id}/`,
  // Провайдерский обзор флота (агрегаты для дашборда):
  // {total_installations, by_health{}, by_agent_version{}, certs_expiring_soon, certs_expired}
  OVERVIEW: "fleet/overview/",
}

/**
 * Лицензии (EyeHub — ISSUER).
 *
 * ВАЖНО: операторские (портальные) операции живут под префиксом `admin/` —
 * роутер регистрирует их как `admin/licenses/`. Пути без префикса
 * (`licenses/activate|validate|deactivate`) — ВНЕШНИЙ контракт клиники
 * (машинный, подписанный ответ), порталом не вызывается.
 */
export const LICENSE_ENDPOINTS = {
  LIST: "admin/licenses/",
  DETAIL: id => `admin/licenses/${id}/`,
  CREATE: "admin/licenses/",
  // Административные переходы состояния (сторона провайдера).
  REVOKE: id => `admin/licenses/${id}/revoke/`,
  SUSPEND: id => `admin/licenses/${id}/suspend/`,
  REINSTATE: id => `admin/licenses/${id}/reinstate/`,
  // Подписки лицензии на AI-модули: список/подключение, удаление, сброс периода.
  MODULES: id => `admin/licenses/${id}/modules/`,
  MODULE_DETAIL: (id, slug) => `admin/licenses/${id}/modules/${slug}/`,
  MODULE_RESET_USAGE: id => `admin/licenses/${id}/modules/reset-usage/`,
}

/**
 * Провижининг — ОПЕРАТОРСКАЯ плоскость портала (hub-admin, cookie-сессия).
 *
 * ВАЖНО: `provisioning/orders/` без префикса `admin/` — МАШИННЫЙ контракт EyeStore
 * (Bearer service-account, роль hub-provisioning). Порталом он не вызывается никогда:
 * у двух ресурсов разные принципалы, и обращение отсюда означало бы человеческий
 * доступ к машинному пути.
 *
 * Ключ пути — `id` записи хаба (UUID), а НЕ `order_id` EyeStore: по `order_id` ищет
 * машинный ресурс (это его ключ идемпотентности). `order_id` остаётся в теле ответа
 * и доступен через `?search=`.
 *
 * `ORDERS` обслуживает и GET (список), и POST (создание заказа оператором): пока
 * коммерческого слоя EyeStore нет, подключить клинику «под ключ» из интерфейса больше
 * нечем. Отдельной константы под создание нет намеренно — путь тот же, и второе имя
 * однажды разъехалось бы с первым.
 */
export const PROVISIONING_ENDPOINTS = {
  ORDERS: "admin/provisioning/orders/",
  ORDER_DETAIL: id => `admin/provisioning/orders/${id}/`,
  // Перезапуск застрявшего заказа: идемпотентен, 409 при идущей саге, 502 при отказе
  // внешней системы. Секретов ответ не содержит (только secrets_reissue_required).
  ORDER_RETRY: id => `admin/provisioning/orders/${id}/retry/`,
  // Перевыпуск одноразовых секретов (§7.6) — ЕДИНСТВЕННЫЙ путь получить их повторно.
  // Ротация, а не показ сохранённого: прежний enrollment-токен отзывается, прежний
  // deploy-токен удаляется в GitLab. Операция аудируется и ограничена по частоте.
  ORDER_REISSUE_SECRETS: id =>
    `admin/provisioning/orders/${id}/reissue-secrets/`,
}

/**
 * Самообслуживание организации (org-admin своей организации, cookie-сессия).
 *
 * Authority по перерасходу принадлежит хабу: значение уезжает в подписанные
 * entitlements ответа `validate`, поэтому переключатель живёт ЗДЕСЬ, а не в админке
 * клиники — управляющего канала клиника→хаб не существует (ADR §7.8).
 *
 * `public_key` этот ресурс не отдаёт: витрине потребления ключ не нужен, а отдать
 * его в браузер значит размножить учётные данные инсталляции по рабочим местам.
 */
export const SELF_SERVICE_ENDPOINTS = {
  LICENSES: "self-service/licenses/",
  MODULE_OVERAGE: (licenseId, moduleSlug) =>
    `self-service/licenses/${licenseId}/modules/${moduleSlug}/overage/`,
  /** Подтверждение единственности копии: снимает подозрение и обнуляет привязку к серверу. */
  REBIND: licenseId => `self-service/licenses/${licenseId}/rebind/`,
}

/** Каталог AI-модулей, доступных для подписки (hub-admin). */
export const MODULE_ENDPOINTS = {
  LIST: "admin/modules/",
  DETAIL: id => `admin/modules/${id}/`,
}

/** Ключи подписи лицензий Ed25519 (hub-admin): список/выпуск/ротация. */
export const SIGNING_KEY_ENDPOINTS = {
  LIST: "admin/keys/",
  DETAIL: id => `admin/keys/${id}/`,
  CREATE: "admin/keys/",
  ROTATE: id => `admin/keys/${id}/rotate/`,
}

/** Федерация: двусторонние связи + подписанная директория пиров. */
export const FEDERATION_ENDPOINTS = {
  LINKS: "federation-links/",
  LINK_DETAIL: id => `federation-links/${id}/`,
  // Двусторонний консент: consent/revoke как detail-actions. Propose = POST LINKS.
  LINK_CONSENT: id => `federation-links/${id}/consent/`,
  LINK_REVOKE: id => `federation-links/${id}/revoke/`,
  DIRECTORY_ENTRIES: "directory-entries/",
  DIRECTORY_ENTRY_DETAIL: id => `directory-entries/${id}/`,
}

/** Поддержка (Ц13): тикеты, комментарии, вложения, окна обслуживания. */
export const SUPPORT_ENDPOINTS = {
  TICKETS: "tickets/",
  TICKET_DETAIL: id => `tickets/${id}/`,
  TICKET_COMMENTS: id => `tickets/${id}/comments/`,
  // Загрузка вложения — POST tickets/{id}/attach/ (требует phi_ack=true).
  // Список вложений приходит вложенным в TICKET_DETAIL (отдельного GET нет).
  TICKET_ATTACH: id => `tickets/${id}/attach/`,
  MAINTENANCE_WINDOWS: "maintenance-windows/",
}

/** Диагностические бандлы (Ц13): видимы только после approve оператором клиники. */
export const DIAGNOSTIC_BUNDLE_ENDPOINTS = {
  LIST: "diagnostic-bundles/",
  DETAIL: id => `diagnostic-bundles/${id}/`,
  CREATE: "diagnostic-bundles/",
  APPROVE: id => `diagnostic-bundles/${id}/approve/`,
}

/** Discovery по email-домену (org + список entry-URL площадок). */
export const DISCOVERY_ENDPOINTS = {
  DISCOVER: "auth/discovery/",
  // Площадки текущего (залогиненного) пользователя — entryUrl + relay_enabled/prefer_internal.
  // Добавляется backend-агентом (FIX_MANIFEST A3). Портал деградирует на discovery, если 404.
  MY_SITES: "my-sites/",
}

/** Окна планового обслуживания (Ц13): список — любой аутентифицированный, создание — провайдер. */
export const MAINTENANCE_ENDPOINTS = {
  LIST: "maintenance-windows/",
  DETAIL: id => `maintenance-windows/${id}/`,
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Строит полный URL для endpoint (для прямых redirect / fetch).
 * @param {string} endpoint
 * @returns {string}
 */
export function buildUrl(endpoint) {
  const baseUrl =
    process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1/"
  return baseUrl + endpoint
}

const API_CONSTANTS = {
  API_BASE_PATH,
  AUTH_ENDPOINTS,
  AUTH_FULL_PATHS,
  ORGANIZATION_ENDPOINTS,
  SITE_ENDPOINTS,
  INSTALLATION_ENDPOINTS,
  ORG_DOMAIN_ENDPOINTS,
  ORG_MEMBERSHIP_ENDPOINTS,
  ENROLLMENT_TOKEN_ENDPOINTS,
  CONNECTOR_ENDPOINTS,
  FLEET_ENDPOINTS,
  LICENSE_ENDPOINTS,
  PROVISIONING_ENDPOINTS,
  SELF_SERVICE_ENDPOINTS,
  MODULE_ENDPOINTS,
  SIGNING_KEY_ENDPOINTS,
  FEDERATION_ENDPOINTS,
  SUPPORT_ENDPOINTS,
  DIAGNOSTIC_BUNDLE_ENDPOINTS,
  DISCOVERY_ENDPOINTS,
  MAINTENANCE_ENDPOINTS,
  buildUrl,
}

export default API_CONSTANTS
